import * as yup from 'yup';

import { Address, requiredAddressSchema } from './address';

const yearsOfOperation = ['Less than 2 years', 'Between 2 and 5 years', 'More than 5 years'] as const;

const yearsOfOperationOptions = yearsOfOperation.map((value) => ({
  id: value,
  name: value,
}));

export type FleetBase = {
  name: string;
  dbaName: string | null;
  address: Address;
  shippingAddress: Address | null;
  numCardsToOrder: number | null;
  ein: string;
  yearsOfOperation: (typeof yearsOfOperation)[number] | '';
  businessType: string;
  industryId: string;
  annualRevenue: number | null;
  fleetSize: number | null;
  monthlyFuelSpend: number | null;
  monthlyNonFuelSpend: number | null;
  defAndVehicleSupplies: boolean | null;
  iceWaterFood: boolean | null;
  fleetAndMaintenance: boolean | null;
  travelAndRestaurants: boolean | null;
  materialsAndSupplies: boolean | null;
  generalBusiness: boolean | null;
  cardRestriction: string | null;
};

export type PartialFleetBase = {
  address: Partial<Address>;
  shippingAddress: Partial<Address>;
} & Omit<Partial<FleetBase>, 'address' | 'shippingAddress'>;

const einNumberFormat = {
  isNumericString: true,
  format: '##-#######',
  mask: '_',
};

export const MIN_NUM_CARDS = 0;
export const MAX_NUM_CARDS = 50;

const fleetSchema: { [key in keyof FleetBase]: yup.AnySchema } = {
  name: yup.string().trim().required('Company name is required').max(128, 'Company name is too long'),
  dbaName: yup
    .string()
    .trim()
    .required('Company name is required')
    .max(24, 'Name on card must be less than 25 characters'),
  ein: yup
    .string()
    .matches(/^\d{9}$/, 'EIN must be entered in the format like 12-1234567')
    .required('EIN is required'),
  address: yup.object(requiredAddressSchema),
  shippingAddress: yup.object(requiredAddressSchema),
  numCardsToOrder: yup
    .number()
    .required('Number of cards is required')
    .min(MIN_NUM_CARDS, 'Minimum cards allowed: 0')
    .max(MAX_NUM_CARDS, 'Max order size is 50 cards. You can order more later.'),
  businessType: yup.string().required('Business Type is required'),
  industryId: yup.string().required('Industry is required'),
  yearsOfOperation: yup
    .string()
    .required('Years of operation is required')
    .oneOf([...yearsOfOperation]),
  annualRevenue: yup
    .number()
    .typeError('Annual business revenue is required')
    .required('Annual business revenue is required')
    .min(1, 'Annual business revenue must be greater than 0'),
  monthlyFuelSpend: yup
    .number()
    .typeError('Expected monthly fuel spend is required')
    .required('Expected monthly fuel spend is required')
    .min(1, 'Expected monthly fuel spend must be greater than 0'),
  fleetSize: yup
    .number()
    .typeError('# of vehicles is required')
    .min(1, 'Must have at least one fleet vehicle')
    .required('# of vehicles is required'),
  monthlyNonFuelSpend: yup
    .number()
    .nullable()
    .min(0, 'Expected monthly non-fuel spend must be greater than or equal to 0'),
  defAndVehicleSupplies: yup.boolean().optional().nullable(),
  iceWaterFood: yup.boolean().optional().nullable(),
  fleetAndMaintenance: yup.boolean(),
  travelAndRestaurants: yup.boolean(),
  materialsAndSupplies: yup.boolean(),
  generalBusiness: yup.boolean().optional().nullable(),
  cardRestriction: yup.string().nullable().required('Please make a gas station restriction selection.'),
};

export { fleetSchema, yearsOfOperationOptions, einNumberFormat };
